import type { ColumnDef } from "@tanstack/react-table";
import {
  dailyActivityColumns,
  periodMetricColumns,
  repColumns,
  syncRunColumns,
  teamColumns,
  teamPeriodMetricColumns,
  type DailyActivityRow,
  type PeriodMetricRow,
  type RepRow,
  type SyncRunRow,
  type TeamPeriodMetricRow,
  type TeamRow,
} from "@/raw/datasets/columns";
import {
  fetchDailyActivities,
  fetchPeriodMetrics,
  fetchReps,
  fetchSyncRuns,
  fetchTeamPeriodMetrics,
  fetchTeams,
} from "@/raw/datasets/queries";
import type { RawRepPickerMode, RawScopeFilterKind } from "@/raw/lib/filter-by-scope";

export type RawDatasetId =
  "sync-runs" | "daily-activities" | "period-metrics" | "team-period-metrics" | "reps" | "teams";

export interface RawDatasetMeta {
  id: RawDatasetId;
  title: string;
  description: string;
  route: string;
  queryKey: readonly [string, RawDatasetId];
}

interface RawDatasetDefinition<TData extends Record<string, unknown>> extends RawDatasetMeta {
  fetchRows: () => Promise<TData[]>;
  columns: ColumnDef<TData, unknown>[];
  searchColumnId?: keyof TData & string;
  exportFilename: string;
  scopeFilter: RawScopeFilterKind;
  /** Rep multi-select behavior when `scopeFilter` is `team-rep`. Defaults to Explore-style `single-team`. */
  repPicker?: RawRepPickerMode;
}

export const rawDatasetMetas: RawDatasetMeta[] = [
  {
    id: "sync-runs",
    title: "Sync runs",
    description: "Ingestion run log — status, row counts, and errors.",
    route: "/raw/sync-runs",
    queryKey: ["raw", "sync-runs"],
  },
  {
    id: "daily-activities",
    title: "Daily activities",
    description: "Calls, connected calls, meetings, and sequences by rep and day.",
    route: "/raw/daily-activities",
    queryKey: ["raw", "daily-activities"],
  },
  {
    id: "period-metrics",
    title: "Period metrics (rep)",
    description: "Sales and opportunities by rep and period.",
    route: "/raw/period-metrics",
    queryKey: ["raw", "period-metrics"],
  },
  {
    id: "team-period-metrics",
    title: "Period metrics (team)",
    description: "Sales and opportunities by team and period.",
    route: "/raw/team-period-metrics",
    queryKey: ["raw", "team-period-metrics"],
  },
  {
    id: "reps",
    title: "Reps",
    description: "Rep dimension with team, region, and Syncore refs.",
    route: "/raw/reps",
    queryKey: ["raw", "reps"],
  },
  {
    id: "teams",
    title: "Teams",
    description: "Team dimension with department and region.",
    route: "/raw/teams",
    queryKey: ["raw", "teams"],
  },
];

export const defaultRawRoute = rawDatasetMetas[0].route;

export const rawDatasets: Record<RawDatasetId, RawDatasetDefinition<Record<string, unknown>>> = {
  "sync-runs": {
    ...rawDatasetMetas.find((dataset) => dataset.id === "sync-runs")!,
    fetchRows: fetchSyncRuns as () => Promise<Record<string, unknown>[]>,
    columns: syncRunColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "source",
    exportFilename: "sync-runs.csv",
    scopeFilter: "none",
  },
  "daily-activities": {
    ...rawDatasetMetas.find((dataset) => dataset.id === "daily-activities")!,
    fetchRows: fetchDailyActivities as () => Promise<Record<string, unknown>[]>,
    columns: dailyActivityColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "rep_name",
    exportFilename: "daily-activities.csv",
    scopeFilter: "team-rep",
  },
  "period-metrics": {
    ...rawDatasetMetas.find((dataset) => dataset.id === "period-metrics")!,
    fetchRows: fetchPeriodMetrics as () => Promise<Record<string, unknown>[]>,
    columns: periodMetricColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "rep_name",
    exportFilename: "period-metrics.csv",
    scopeFilter: "team-rep",
    repPicker: "always",
  },
  "team-period-metrics": {
    ...rawDatasetMetas.find((dataset) => dataset.id === "team-period-metrics")!,
    fetchRows: fetchTeamPeriodMetrics as () => Promise<Record<string, unknown>[]>,
    columns: teamPeriodMetricColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "team_name",
    exportFilename: "team-period-metrics.csv",
    scopeFilter: "team",
  },
  reps: {
    ...rawDatasetMetas.find((dataset) => dataset.id === "reps")!,
    fetchRows: fetchReps as () => Promise<Record<string, unknown>[]>,
    columns: repColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "full_name",
    exportFilename: "reps.csv",
    scopeFilter: "team-rep",
    repPicker: "always",
  },
  teams: {
    ...rawDatasetMetas.find((dataset) => dataset.id === "teams")!,
    fetchRows: fetchTeams as () => Promise<Record<string, unknown>[]>,
    columns: teamColumns as ColumnDef<Record<string, unknown>, unknown>[],
    searchColumnId: "name",
    exportFilename: "teams.csv",
    scopeFilter: "team",
  },
};

export function getRawDataset(id: RawDatasetId): RawDatasetDefinition<Record<string, unknown>> {
  return rawDatasets[id];
}

// Typed exports for tests or future strict pages
export type RawDatasetRowMap = {
  "sync-runs": SyncRunRow;
  "daily-activities": DailyActivityRow;
  "period-metrics": PeriodMetricRow;
  "team-period-metrics": TeamPeriodMetricRow;
  reps: RepRow;
  teams: TeamRow;
};
